import { useState } from 'react';
import { navigate } from '@/app/router';
import { floors } from '@/content/floors';
import {
  clearElevatorBeat,
  forgetReported,
  markReported,
  peekElevatorBeat,
  reportedBefore,
  setElevatorBeat,
  type ElevatorBeat,
} from '@/game/beats';
import { letterCount } from '@/game/codename';
import { forgetIntro, introSeen, markIntroSeen } from '@/game/intro';
import { EMPTY, PASS_STARS, type FloorProgress, type Stars } from '@/game/progress';
import { progressStore, useProgress } from '@/game/progressStore';
import { resetGame } from '@/game/reset';
import { AGES, settingsStore, useSettings } from '@/game/settings';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { Card, Seg } from './ui';
import { useToast } from './util';

const STARS: readonly Stars[] = [0, 1, 2, 3];
const BEATS = ['none', 'next', 'allDone'] as const;

/**
 * The active player's game state: age, story flags, presets, and each floor's stars, letters and
 * mastery, with a way into each mission.
 */
export function ProgressPanel() {
  const toast = useToast();
  const progress = useProgress();
  const { age } = useSettings();
  const [intro, setIntro] = useState(introSeen);
  const [reported, setReported] = useState(reportedBefore);
  const [beat, setBeat] = useState<ElevatorBeat | 'none'>(() => peekElevatorBeat() ?? 'none');
  const [confirmReset, setConfirmReset] = useState(false);

  const setFloor = (id: string, change: Partial<FloorProgress>) => {
    const current = progress[id] ?? EMPTY;
    progressStore.reset({ ...progress, [id]: { ...current, ...change } });
  };
  /** The first `done` floors finished with `stars` and their codenames uncovered. */
  const preset = (done: number, stars: Stars = PASS_STARS) => {
    const next: Record<string, FloorProgress> = {};
    floors.slice(0, done).forEach((floor) => {
      next[floor.id] = {
        ...EMPTY,
        stars,
        completions: 1,
        revealed: letterCount(floor.foe.codename),
        bestCorrect: floor.words.length,
      };
    });
    progressStore.reset(next);
    markIntroSeen();
    setIntro(true);
    toast(done === 0 ? 'Progress cleared (intro seen)' : `Floors 1–${String(done)} done`);
  };

  return (
    <>
      <div className={s.grid2}>
        <Card title="Player">
          <div className={s.row}>
            <span className={s.label}>Age</span>
            <Seg
              label="Age"
              options={AGES}
              value={age}
              format={(a) => (a === 13 ? '13+' : String(a))}
              onChange={(a) => {
                settingsStore.setAge(a);
              }}
            />
          </div>
          <div className={s.row}>
            <span className={s.label}>Story</span>
            <label className={s.check}>
              <input
                type="checkbox"
                checked={intro}
                onChange={(e) => {
                  if (e.target.checked) markIntroSeen();
                  else forgetIntro();
                  setIntro(e.target.checked);
                }}
              />
              Intro seen
            </label>
            <label className={s.check}>
              <input
                type="checkbox"
                checked={reported}
                onChange={(e) => {
                  if (e.target.checked) markReported();
                  else forgetReported();
                  setReported(e.target.checked);
                }}
              />
              First mission report shown
            </label>
          </div>
          <div className={s.row}>
            <span className={s.label}>Elevator beat</span>
            <Seg
              label="Story beat waiting at the elevator"
              options={BEATS}
              value={beat}
              format={(b) => (b === 'allDone' ? 'all done' : b)}
              onChange={(b) => {
                if (b === 'none') clearElevatorBeat();
                else setElevatorBeat(b);
                setBeat(b);
              }}
            />
          </div>
        </Card>

        <Card title="Presets">
          <div className={s.row}>
            <button
              type="button"
              className={s.btn}
              onClick={() => {
                preset(0);
              }}
            >
              No progress
            </button>
            {floors.map((floor, i) => (
              <button
                key={floor.id}
                type="button"
                className={s.btn}
                onClick={() => {
                  preset(i + 1);
                }}
              >
                1–{i + 1} done
              </button>
            ))}
            <button
              type="button"
              className={s.btn}
              onClick={() => {
                preset(floors.length, 3);
              }}
            >
              All ★★★
            </button>
          </div>
          <div className={s.row}>
            {confirmReset ? (
              <>
                <span>Clear this player's game (age kept) and reload?</span>
                <button
                  type="button"
                  className={cx(s.btn, s.dangerSolid)}
                  onClick={() => {
                    resetGame();
                  }}
                >
                  Start over
                </button>
                <button
                  type="button"
                  className={s.btn}
                  onClick={() => {
                    setConfirmReset(false);
                  }}
                >
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                className={cx(s.btn, s.danger)}
                onClick={() => {
                  setConfirmReset(true);
                }}
              >
                Start over…
              </button>
            )}
          </div>
        </Card>
      </div>

      <Card title="Floors" flush>
        <div className={s.tableWrap}>
          <table className={cx(s.table, s.stack)}>
            <thead>
              <tr>
                <th>Floor</th>
                <th>Stars</th>
                <th>Letters</th>
                <th className={s.num}>Runs</th>
                <th className={s.num}>Best</th>
                <th className={s.num}>Mastery</th>
                <th>
                  <span className={s.visuallyHidden}>Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {floors.map((floor) => {
                const p = progress[floor.id] ?? EMPTY;
                const total = letterCount(floor.foe.codename);
                return (
                  <tr key={floor.id}>
                    <td>
                      <strong>{floor.number}.</strong> {floor.foe.codename}
                      <div className={s.hint}>{floor.focus}</div>
                    </td>
                    <td data-label="Stars">
                      <Seg
                        label={`Stars on floor ${String(floor.number)}`}
                        options={STARS}
                        value={p.stars}
                        onChange={(stars) => {
                          setFloor(floor.id, {
                            stars,
                            completions: Math.max(p.completions, stars ? 1 : 0),
                          });
                        }}
                      />
                    </td>
                    <td data-label="Letters">
                      <div className={s.row} style={{ flexWrap: 'nowrap' }}>
                        <input
                          type="range"
                          min={0}
                          max={total}
                          value={p.revealed}
                          aria-label={`Letters uncovered on floor ${String(floor.number)}`}
                          onChange={(e) => {
                            setFloor(floor.id, { revealed: Number(e.target.value) });
                          }}
                          style={{ width: 110, accentColor: 'var(--accent)' }}
                        />
                        <span className={s.mono}>
                          {p.revealed}/{total}
                        </span>
                      </div>
                    </td>
                    <td data-label="Runs" className={s.num}>
                      {p.completions}
                    </td>
                    <td data-label="Best" className={s.num}>
                      {p.bestCorrect}/{floor.words.length}
                    </td>
                    <td data-label="Mastery" className={s.num}>
                      {p.mastery ? (
                        <span title={`${String(p.mastery.attempts)} attempts`}>
                          {Math.round(p.mastery.p * 100)}%
                        </span>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={cx(s.btn, s.small, s.primary)}
                        onClick={() => {
                          navigate({ page: 'floor', floorId: floor.id, run: String(Date.now()) });
                        }}
                      >
                        Play
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
